/**
 * CC-027 — migration-status badge.
 *
 * Renders one of four badges off a token's `dexStatus` + `isGraduated`:
 *
 *   🟢 Сбор             — on the bonding curve, collecting buys
 *   🟡 Миграция          — migration in flight (any intermediate state)
 *   ❌ Ошибка миграции   — migration failed, needs operator attention
 *   ✅ Торгуется на DEX  — graduated; CLICKABLE → STON.fi swap (referral)
 *
 * Legacy tokens (`dexStatus === null`) that aren't graduated render no
 * badge at all.
 *
 * Shared by TokenCard (Home feeds) and the Token detail header so the
 * status mapping lives in exactly one place.
 */

import { useState, type CSSProperties } from 'react';
import type { DexStatus } from '@memepad/shared';
import { stonfiSwapUrl } from '../lib/dex';

/** Minimal token shape the badge needs — works for every call-site. */
export interface DexBadgeInput {
  dexStatus: DexStatus | null;
  isGraduated: boolean;
  masterAddr: string;
}

type BadgeColor = 'green' | 'yellow' | 'red' | 'blue';

interface BadgeData {
  color: BadgeColor;
  text: string;
  /** Present only on the ✅ badge — STON.fi swap deep-link. */
  href?: string;
}

/** Compile-time exhaustiveness guard — see dexStatusToBadge's default. */
function assertNever(x: never): never {
  throw new Error(`unhandled dex_status: ${String(x)}`);
}

/**
 * Pure mapping: token status → badge, or null for "no badge".
 *
 * Implemented as a `switch` over `dexStatus` with `default: assertNever`
 * so adding a value to the `DexStatus` union (or removing a `case`)
 * fails the build until this function handles it.
 */
export function dexStatusToBadge(t: DexBadgeInput): BadgeData | null {
  const graduatedBadge: BadgeData = {
    color: 'blue',
    text: '✅ Торгуется на DEX',
    href: stonfiSwapUrl(t.masterAddr),
  };
  switch (t.dexStatus) {
    case 'idle':
      // A token can read `is_graduated` on-chain while dex_status still
      // lags at 'idle' — honour isGraduated either way.
      return t.isGraduated
        ? graduatedBadge
        : { color: 'green', text: '🟢 Сбор' };
    case 'graduating':
    case 'migrating':
    case 'lp_creating':
    case 'lp_committing':
    case 'lp_burning':
      return { color: 'yellow', text: '🟡 Миграция' };
    case 'migration_failed':
      return { color: 'red', text: '❌ Ошибка миграции' };
    case 'graduated':
      return graduatedBadge;
    case null:
      // Legacy token, no dex_status. Still show the DEX badge if the
      // curve graduated on-chain; otherwise render nothing.
      return t.isGraduated ? graduatedBadge : null;
    default:
      return assertNever(t.dexStatus);
  }
}

const BADGE_COLORS: Record<BadgeColor, { bg: string; bgHover: string; fg: string }> = {
  green: { bg: 'rgba(34,197,94,0.20)', bgHover: 'rgba(34,197,94,0.34)', fg: '#22c55e' },
  yellow: { bg: 'rgba(234,179,8,0.20)', bgHover: 'rgba(234,179,8,0.34)', fg: '#eab308' },
  red: { bg: 'rgba(239,68,68,0.20)', bgHover: 'rgba(239,68,68,0.34)', fg: '#ef4444' },
  blue: { bg: 'rgba(59,130,246,0.20)', bgHover: 'rgba(59,130,246,0.34)', fg: '#3b82f6' },
};

/** Open a URL via the Telegram Mini App SDK when available, else a tab. */
function openExternal(url: string): void {
  const tg = window.Telegram?.WebApp;
  if (tg && typeof tg.openLink === 'function') {
    tg.openLink(url);
  } else {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}

/**
 * The badge element. The ✅ variant is a clickable link (cursor pointer +
 * hover brighten) — it stops event propagation so a click doesn't also
 * trigger the surrounding <Link> card navigation.
 *
 * CC-040 — pass `interactive={false}` to render the ✅ variant as a plain
 * visual label (no link). The Token detail page does this: it shows the
 * badge purely as a status indicator and offers explicit STON.fi / OKX
 * trade buttons instead. TokenCard (Home feed) leaves it interactive.
 */
export function DexBadge(props: DexBadgeInput & { interactive?: boolean }) {
  const [hover, setHover] = useState(false);
  const badge = dexStatusToBadge(props);
  if (!badge) return null;

  const c = BADGE_COLORS[badge.color];
  const clickable = badge.href !== undefined && props.interactive !== false;
  const style: CSSProperties = {
    display: 'inline-block',
    padding: '3px 8px',
    borderRadius: 7,
    fontSize: 12,
    fontWeight: 600,
    lineHeight: 1.4,
    whiteSpace: 'nowrap',
    color: c.fg,
    background: clickable && hover ? c.bgHover : c.bg,
    cursor: clickable ? 'pointer' : 'default',
  };

  if (!clickable) {
    return <span style={style}>{badge.text}</span>;
  }

  const href = badge.href!;
  return (
    <span
      role="link"
      tabIndex={0}
      title="Открыть на STON.fi"
      style={style}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={(e) => {
        // Inside a react-router <Link> — don't navigate the card.
        e.preventDefault();
        e.stopPropagation();
        openExternal(href);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.stopPropagation();
          openExternal(href);
        }
      }}
    >
      {badge.text}
    </span>
  );
}
